import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft } from "lucide-react";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LinkAsButton } from "@/components/LinkAsButton";
import { EntityListPage } from "@/components/entity-page/EntityListPage";
import { getFormOptions } from "@/lib/api";
import type { FormOption } from "@/types";

export const Route = createFileRoute("/dashboard/settings/models/")({
  component: ModelsIndexPage,
});

function ModelsIndexPage() {
  return <ModelsIndexPageComponent />;
}

// Exported for testing
export function ModelsIndexPageComponent() {
  const [models, setModels] = useState<FormOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    getFormOptions("models")
      .then((options) => {
        if (!active) return;
        setModels(options);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error fetching models:", err);
        setError("Failed to load models.");
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return (
    <EntityListPage
      title="Models"
      description="The LLM models available to agents, including the exact model identifier sent to the provider API - each option here populates an agent's modelId field."
      items={models}
      loading={loading}
      error={error}
      getItemKey={(model) => model.id}
      renderItem={(model) => (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>{model.label}</CardTitle>
                <CardDescription>
                  {model.description || `Value: ${model.value}`}
                </CardDescription>
                <p className="text-xs text-muted-foreground mt-1">
                  Order: {model.order ?? 0}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Model: {model.model}</p>
              </div>
              <Button variant="outline" size="sm" asChild>
                <Link to="/dashboard/settings/models/$id" params={{ id: model.id }}>
                  Edit
                </Link>
              </Button>
            </div>
          </CardHeader>
        </Card>
      )}
      createHref="/dashboard/settings/models/new"
      createLabel="Create Model"
      emptyMessage="No models yet. Create one to get started."
      headerActions={
        <LinkAsButton to="/dashboard/settings" variant="ghost" size="sm">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Settings
        </LinkAsButton>
      }
    />
  );
}
